import { type FC, useMemo, useState } from "react";

import { ArrowSquareInIcon } from "@phosphor-icons/react";
import moment from "moment";

import {
  Badge,
  type DateRangePartial,
  DateRangePicker,
  Text,
} from "@alpacahq/ui/old";
import {
  Table,
  type TableColumn,
  useDataGrid,
  useTableFilters,
} from "@alpacahq/ui/old/datagrid";

import type { AcatsTransfer, ContraBroker } from "~/v2/api/rest/acats";
import {
  ACATS_TRANSFER_STATUS_BADGES,
  ACATS_TRANSFER_TYPE_MAP,
} from "~/v2/constants";
import { getPrettyDate } from "~/v2/utils/formatting";
import { RejectionDetailsModal } from "./RejectionDetailsModal";
import { TransferDetailsModal } from "./TransferDetailsModal";
import { renderTransferDirection } from "./utils";

type PortfolioTransferTableProps = {
  transfers: AcatsTransfer[];
  accountId: string;
  contraBrokers: ContraBroker[];
};

export const PortfolioTransferTable: FC<PortfolioTransferTableProps> = ({
  transfers,
  accountId,
  contraBrokers,
}) => {
  const [dateRange, setDateRange] = useState<DateRangePartial | undefined>();

  // Create a mapping from DTC number to broker name from the passed prop
  const brokerNameMap = useMemo(() => {
    const map = new Map<string, string>();
    contraBrokers.forEach((broker) => {
      // Map the 4-digit DTC number
      map.set(broker.number, broker.name);
    });

    return map;
  }, [contraBrokers]);

  const [transferDetailsModal, setTransferDetailsModal] = useState<{
    isOpen: boolean;
    transfer: AcatsTransfer | null;
  }>({ isOpen: false, transfer: null });

  const [rejectionDetailsModal, setRejectionDetailsModal] = useState<{
    isOpen: boolean;
    transfer: AcatsTransfer | null;
  }>({ isOpen: false, transfer: null });

  const filteredTransfers = useMemo(() => {
    let filtered = [...transfers];

    // Apply date range filter
    if (dateRange?.[0] && dateRange[1]) {
      const [startDate, endDate] = dateRange;
      filtered = filtered.filter((transfer) => {
        const transferDate = moment(transfer.created_at);
        return transferDate.isBetween(
          moment(startDate),
          moment(endDate),
          "day",
          "[]",
        );
      });
    }

    // Sort by date (newest first)
    return filtered.sort((a, b) =>
      moment(b.created_at).diff(moment(a.created_at)),
    );
  }, [transfers, dateRange]);

  // Memoize the options objects to prevent recreating them on every render
  const fromOptions = useMemo(
    () => ({
      direction: "FROM" as const,
      className: "pt-2 pb-2",
      brokerNameMap,
    }),
    [brokerNameMap],
  );

  const toOptions = useMemo(
    () => ({
      direction: "TO" as const,
      className: "pt-2 pb-2",
      brokerNameMap,
    }),
    [brokerNameMap],
  );

  // Memoize cell renderers to prevent recreating them
  const renderFromCell = useMemo(
    () => (transfer: AcatsTransfer) =>
      renderTransferDirection(transfer, fromOptions),
    [fromOptions],
  );

  const renderToCell = useMemo(
    () => (transfer: AcatsTransfer) =>
      renderTransferDirection(transfer, toOptions),
    [toOptions],
  );

  const renderAmountCell = useMemo(
    () => (transfer: AcatsTransfer) => {
      const transferType =
        ACATS_TRANSFER_TYPE_MAP[transfer.transfer_type] || "-";

      return (
        <button
          type="button"
          className="flex items-center gap-0 text-gray-800 transition-colors hover:text-gray-600"
          onClick={() => {
            setTransferDetailsModal({ isOpen: true, transfer });
          }}
        >
          <Text className="text-sm">{transferType}</Text>
          <ArrowSquareInIcon size={20} />
        </button>
      );
    },
    [],
  );

  const renderStatusCell = useMemo(
    () => (transfer: AcatsTransfer) => {
      // A transfer that fails schema validation is passed through raw, so
      // `status` may be missing or carry a status we have no badge for.
      const statusConfig = transfer.status
        ? ACATS_TRANSFER_STATUS_BADGES[transfer.status.transfer_status]
        : undefined;
      if (!statusConfig) {
        return <Badge variant="v2/gray">Unknown</Badge>;
      }
      return <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>;
    },
    [],
  );

  const renderNoteCell = useMemo(
    () => (transfer: AcatsTransfer) => {
      if (transfer.status?.transfer_status === "REJECTED") {
        return (
          <button
            type="button"
            className="text-indigo-600 text-sm underline transition-colors hover:text-indigo-800"
            onClick={() => {
              setRejectionDetailsModal({ isOpen: true, transfer });
            }}
          >
            View
          </button>
        );
      }
      return <Text className="text-sm">-</Text>;
    },
    [],
  );

  const columns = useMemo<TableColumn<AcatsTransfer>[]>(
    () => [
      {
        name: "Date",
        key: "created_at",
        cellRenderer: ({ created_at }) => (
          <Text className="text-sm">
            {getPrettyDate(moment(created_at).toDate().getTime())}
          </Text>
        ),
      },
      {
        name: "From",
        key: "from",
        cellRenderer: renderFromCell,
      },
      {
        name: "To",
        key: "to",
        cellRenderer: renderToCell,
      },
      {
        name: "Amount",
        key: "amount",
        cellRenderer: renderAmountCell,
      },
      {
        name: "Status",
        key: "status",
        cellRenderer: renderStatusCell,
      },
      {
        name: "Note",
        key: "note",
        cellRenderer: renderNoteCell,
      },
    ],
    [
      renderFromCell,
      renderToCell,
      renderAmountCell,
      renderStatusCell,
      renderNoteCell,
    ],
  );

  const filters = useTableFilters({
    enabled: true,
    columns,
    rows: filteredTransfers,
    filters: [
      {
        key: "status",
        label: "Status",
        options: [
          { label: "Pending", value: "PENDING" },
          { label: "Rejected", value: "REJECTED" },
          { label: "Completed", value: "SETTLED" },
        ],
        filterFn: (row, filterValue) => {
          const values = Array.isArray(filterValue)
            ? filterValue
            : [filterValue];
          return values.some((value) => {
            // Handle "Pending" filter to include all pending statuses
            if (value === "PENDING") {
              return ["DRAFT", "PENDING", "IN_PROGRESS"].includes(
                row.status.transfer_status,
              );
            }
            return value === row.status.transfer_status;
          });
        },
      },
      {
        key: "direction",
        label: "Direction",
        options: [
          { label: "Incoming", value: "incoming" },
          { label: "Outgoing", value: "outgoing" },
        ],
        filterFn: (row, filterValue) =>
          (Array.isArray(filterValue) ? filterValue : [filterValue]).some(
            (value) => value === row.direction,
          ),
      },
    ],
  });

  const handleDateChange = (dates: DateRangePartial) => {
    setDateRange(dates);
  };

  const handleClearDate = () => {
    setDateRange(undefined);
  };

  const tableProps = useDataGrid({
    rows: filteredTransfers,
    columns,
    pagination: {
      defaultPageSize: 10,
    },
    search: {
      searchPlaceholder: "Search",
      searchColumns: [
        "contra_broker_name",
        "account_number",
        "contra_account_number",
        "transfer_type",
        "account_name",
        "acats_id",
      ],
    },
    filters: filters,
    sort: true,
  });

  return (
    <>
      <Table
        {...tableProps}
        emptyMessage="No portfolio transfers found"
        tools={[
          <div className="z-20" key="date-picker">
            <DateRangePicker
              placeholder="Select Date Range"
              onChange={handleDateChange}
              value={dateRange || [null, null]}
              isClearable
              onClear={handleClearDate}
              className="p-2.5"
            />
          </div>,
        ]}
      />

      <TransferDetailsModal
        isOpen={transferDetailsModal.isOpen}
        onClose={() =>
          setTransferDetailsModal({ isOpen: false, transfer: null })
        }
        transfer={transferDetailsModal.transfer}
        accountId={accountId}
        contraBrokers={contraBrokers}
      />

      <RejectionDetailsModal
        isOpen={rejectionDetailsModal.isOpen}
        onClose={() =>
          setRejectionDetailsModal({ isOpen: false, transfer: null })
        }
        transfer={rejectionDetailsModal.transfer}
      />
    </>
  );
};
